import { useEffect, useState } from "react";
import { api, formatCurrency } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, FileText, Clock, Calendar } from "lucide-react";
import TimeDeltaBadge from "@/components/TimeDeltaBadge";
import ProfitabilityBadge from "@/components/ProfitabilityBadge";

const STATUS_COLORS = {
  pending: "bg-slate-100 text-slate-800",
  in_progress: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

export const OrdersTab = ({ employee }) => {
  const [data, setData] = useState({ items: [], total_hours: 0, total_days: 0 });
  const [profitability, setProfitability] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!employee?.id) return;
    setLoading(true);
    Promise.all([
      api.getEmployeeOrders(employee.id),
      api.getOrdersProfitability().catch(() => ({ data: {} })),
    ])
      .then(([ordersRes, profitRes]) => {
        setData(ordersRes.data);
        setProfitability(profitRes.data || {});
      })
      .catch(() => setData({ items: [], total_hours: 0, total_days: 0 }))
      .finally(() => setLoading(false));
  }, [employee?.id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card><CardContent className="pt-4">
          <div className="text-xs text-muted-foreground">Chantiers</div>
          <div className="text-2xl font-bold text-indigo-700 flex items-center gap-2">
            <FileText className="h-5 w-5" />{data.items.length}
          </div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-xs text-muted-foreground">Jours travaillés</div>
          <div className="text-2xl font-bold flex items-center gap-2">
            <Calendar className="h-5 w-5 text-secondary" />{data.total_days}
          </div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-xs text-muted-foreground">Heures totales</div>
          <div className="text-2xl font-bold flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />{data.total_hours} h
          </div>
        </CardContent></Card>
      </div>

      <Card>
        <CardContent className="pt-4">
          {data.items.length === 0 ? (
            <p className="text-center text-muted-foreground py-8" data-testid="employee-orders-empty">
              Aucun chantier enregistré pour ce salarié.
            </p>
          ) : (
            <Table data-testid="employee-orders-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Référence</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Ville</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Période</TableHead>
                  <TableHead className="text-right">Jours</TableHead>
                  <TableHead className="text-right">Heures</TableHead>
                  <TableHead className="text-right">CA HT</TableHead>
                  <TableHead>Écart / théorique</TableHead>
                  <TableHead>Rentabilité</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((o) => (
                  <TableRow key={o.order_id} data-testid={`employee-order-row-${o.order_id}`}>
                    <TableCell className="font-mono text-xs">{o.reference || "—"}</TableCell>
                    <TableCell>{o.client_name || "—"}</TableCell>
                    <TableCell className="text-muted-foreground text-xs">{o.city || "—"}</TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[o.status] || STATUS_COLORS.pending}>
                        {o.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {o.first_date}{o.first_date !== o.last_date ? ` → ${o.last_date}` : ""}
                    </TableCell>
                    <TableCell className="text-right font-mono">{o.days_worked}</TableCell>
                    <TableCell className="text-right font-mono">{o.total_hours} h</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(o.amount_ht || 0)}</TableCell>
                    <TableCell><TimeDeltaBadge delta={o.time_delta} compact /></TableCell>
                    <TableCell>
                      {profitability[o.order_id]
                        ? <ProfitabilityBadge data={profitability[o.order_id]} compact />
                        : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OrdersTab;
